<template>
  <Card class="pa-2 mr-1 ml-1 subheading" :style="{ height: cardHeight }">
    <div id="info-header">
      <span class="subheading">{{ $t("Weather Station") }}</span>
      {{ $t("Live Data") }}
    </div>
    <div class="header ws-data-card" :style="{ height: cardHeight }">
      <div v-for="items in wsItems" :key="items.id">
        <span v-if="Object.keys(items).length > 1">
          <div class="pl-2 pt-2">{{ items.name }}</div>
          <v-list class="ma-2 ws-table primary">
            <template
            v-for="(param, index) in Object.keys(items).filter(
              (el) => el !== 'id' && el !== 'name'
            )"
            >
              <v-list-tile :key="`${items.id}_${param}`">
                <v-flex md9>
                  <v-list-tile-content>
                    <v-list-tile-title
                      >{{ computedNameObject[items.id][param].name }}
                      <span class="grey--text caption"
                        >({{ computedNameObject[items.id][param].unit }})</span
                      >
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-flex>
                <v-flex md3>
                  <v-list-tile-content>
                    <v-list-tile-title
                      :class="
                        items[param] == 'No Data'
                          ? 'error--text text-xs-center'
                          : 'number--text text-xs-center'
                      "
                    >
                      {{
                        items[param] == "No Data" ? $t("No Data") : items[param]
                      }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-flex>
              </v-list-tile>
              <v-divider
                v-if="
                  index + 1 <
                  Object.keys(items).filter((el) => el !== 'id' && el !== 'name')
                    .length
                "
                :key="`div_${items.id}_${param}`"
              ></v-divider>
            </template>
          </v-list>
        </span>
      </div>
    </div>
  </Card>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import LiveDb from "@/services/LiveDb.js";
import { mapGettersMixin } from "@/util/mixin/index";
import { liveDeviceMqttMixin } from "@/util/mixin/liveDeviceMqtt";

export default {
  mixins: [mapGettersMixin, liveDeviceMqttMixin],
  props: {
    nameMap: {
      type: Object,
      default: function () {
        return {};
      },
    },
    parameter: {
      type: Array,
      default: function () {
        return [];
      },
    },
    parameterNameMap: {
      type: Object,
      default: function () {
        return {};
      },
    },
    cardHeight: {
      type: [String, Number],
      default: "calc(100vh - 705px)",
    },
  },
  data() {
    return {
      wsItems: [],
      nameObject: {},
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId"]),
    computedNameObject() {
      const cloneNameObject = _.cloneDeep(this.nameObject);
      this.wsItems.forEach((ws) => {
        this.parameter.forEach((param) => {
          if (
            cloneNameObject[ws.id] &&
            cloneNameObject[ws.id][param] &&
            cloneNameObject[ws.id][param].name
          ) {
            cloneNameObject[ws.id][param].name = this.$t(
              cloneNameObject[ws.id][param].name
            );
          }
        });
      });

      return cloneNameObject;
    },
  },
  async mounted() {
    await this.loadWsForPlant(this.getSelectedPlantId);
  },
  methods: {
    isFloat(value) {
      if (
        typeof value === "number" &&
        !Number.isNaN(value) &&
        !Number.isInteger(value)
      ) {
        return true;
      }

      return false;
    },
    toThreeDecimalPlaces(props) {
      if (props !== undefined && props !== null) {
        if (this.isFloat(Number(props))) {
          return props == 0 ? 0 : Number.parseFloat(props).toFixed(3);
        } else {
          return props;
        }
      } else {
        return "No Data";
      }
    },
    async loadWsForPlant(plantId) {
      const loadRequest = this.beginLiveDeviceLoad(plantId);

      try {
        const { wsItems, nameObject } = await this.liveWsDataHandler(plantId);
        if (!this.isLiveDeviceLoadCurrent(loadRequest)) return;

        this.wsItems = wsItems;
        this.nameObject = nameObject;
        this.mqttHandler(plantId);
      } catch (error) {
        if (this.isLiveDeviceLoadCurrent(loadRequest)) {
          console.error("Error loading Live Dashboard weather station data", error);
        }
      }
    },
    async liveWsDataHandler(plantId = this.getSelectedPlantId) {
      // Querying live inverter data
      let wsParamJson = {};
      this.parameter.forEach((key) => {
        wsParamJson[key] = key;
      });
      const wsParam = Buffer.from(JSON.stringify(wsParamJson)).toString(
        "base64"
      );
      const liveWsData = await LiveDb.getLiveDeviceData(
        plantId,
        "weather station",
        wsParam
      );
      const liveData = liveWsData.data.message || {};
      const nameObject = {};

      const wsItems = Object.keys(this.parameterNameMap).map((idConfigured) => {
        const tempObj = liveData[idConfigured]
          ? { ...liveData[idConfigured] }
          : {};
        tempObj["name"] = this.nameMap[idConfigured];
        Object.keys(tempObj)
          .filter((key) => key !== "id" && key !== "name")
          .forEach((dataKey) => {
            if (
              this.parameterNameMap[idConfigured][0].config[dataKey] &&
              this.parameterNameMap[idConfigured][0].config[dataKey].count &&
              this.parameterNameMap[idConfigured][0].config[dataKey].count > 1
            ) {
              tempObj[dataKey].split(",").forEach((data, index) => {
                const newKey = `${dataKey}-${index + 1}`;
                tempObj[newKey] = this.toThreeDecimalPlaces(data);
              });
              delete tempObj[dataKey];
            } else {
              tempObj[dataKey] = this.toThreeDecimalPlaces(tempObj[dataKey]);
            }
          });

        // Parameter Header
        const paramObj = {};
        paramObj[idConfigured] = {};
        this.parameter.forEach((param) => {
          if (
            this.parameterNameMap[idConfigured][0].config[param] &&
            this.parameterNameMap[idConfigured][0].config[param].count &&
            this.parameterNameMap[idConfigured][0].config[param].count > 1
          ) {
            this.parameterNameMap[idConfigured][0].config[
              param
            ].nameMap.forEach((key, idx) => {
              const obj = {};
              const name = key;
              const unit = this.parameterNameMap[idConfigured][0].config[param]
                .unit
                ? this.parameterNameMap[idConfigured][0].config[param].unit
                : "";
              obj["name"] = name;
              obj["unit"] = unit;
              const newParameter = `${param}-${idx + 1}`;
              paramObj[newParameter] = obj;
            });
          } else {
            const obj = {};
            const name =
              this.parameterNameMap[idConfigured][0].config[param].name;
            const unit = this.parameterNameMap[idConfigured][0].config[param]
              .unit
              ? this.parameterNameMap[idConfigured][0].config[param].unit
              : "";
            obj["name"] = name;
            obj["unit"] = unit;
            paramObj[param] = obj;
          }
        });
        nameObject[idConfigured] = paramObj;
        return tempObj;
      });

      return { wsItems, nameObject };
    },
    mqttHandler(plantId = this.getSelectedPlantId) {
      this.startLiveDeviceMqtt(
        "weather station",
        this.updateWsFromMqtt,
        plantId
      );
    },
    updateWsFromMqtt(mqttData) {
      if (!mqttData || Number(mqttData.ignore_status) === 1) return;

      const index = this.wsItems.findIndex(
        station => String(station.id) === String(mqttData.device_id)
      );
      if (index === -1) return;

      const station = this.wsItems[index];
      const deviceConfig = this.parameterNameMap[station.id] &&
        this.parameterNameMap[station.id][0]
        ? this.parameterNameMap[station.id][0].config || {}
        : {};

      this.parameter.forEach((key) => {
        if (mqttData[key] === undefined) return;

        if (deviceConfig[key] && deviceConfig[key].count > 1) {
          String(mqttData[key]).split(",").forEach((data, valueIndex) => {
            this.$set(
              station,
              `${key}-${valueIndex + 1}`,
              this.toThreeDecimalPlaces(data)
            );
          });
        } else {
          this.$set(
            station,
            key,
            this.toThreeDecimalPlaces(mqttData[key])
          );
        }
      });
    },
  },
  watch: {
    getSelectedPlantId() {
      this.resetLiveDeviceLifecycle();
      this.wsItems = [];
      this.nameObject = {};
    },
    async parameterNameMap(newVal) {
      if (newVal && Object.keys(newVal).length > 0) {
        await this.loadWsForPlant(this.getSelectedPlantId);
      }
    },
  },
};
</script>

<style scoped>
#info-header {
  padding-bottom: 16px;
}

#info-header span {
  border-bottom: 2px solid #417cde;
  display: inline-block;
  vertical-align: top;
}

.ws-data-card {
  overflow-x: auto;
  overflow-y: auto;
}

.ws-table {
  border: 1px solid #38405c !important;
}
</style>
